/**
 * Control API client.
 *
 * One place owns: the base URL, `credentials: "include"` (the session is an HTTP-only cookie
 * issued by Better Auth on a different origin), and the error envelope from
 * docs/API-CONTRACT.md. Callers get a typed value or an `ApiError` — never a raw Response.
 *
 * `VITE_API_BASE_URL` is empty in dev, where Vite proxies /api to the API on the same origin.
 */
import type {
  CancelServiceResult,
  ConnectionsResponse,
  ConsentDecision,
  ConsentRequest,
  DeleteRequestResult,
  DeploymentConnectionsSummary,
  DeploymentStatusResponse,
  DeploymentView,
  ExportEstimate,
  ExportJob,
  GithubTransfer,
  GithubTransferState,
  IssuedConnection,
  Me,
  PairingComplete,
  PairingStart,
  ReactivateResult,
} from "./types.js";

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

export type ApiErrorCode =
  | "invalid_request"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "rate_limited"
  | "invalid_state_transition"
  | "not_implemented"
  | "internal"
  | "network";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | undefined;

  constructor(init: {
    code: ApiErrorCode;
    status: number;
    message: string;
    requestId?: string | null;
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId ?? null;
    this.details = init.details;
  }

  /** True when the only sensible UI is "sign in again". */
  get isAuth(): boolean {
    return this.code === "unauthorized";
  }

  /** Copy for a customer. Provider text never reaches here (contract §16.2). */
  get customerMessage(): string {
    if (this.code === "network") return "We could not reach the Construct service. Check your connection and try again.";
    if (this.code === "not_implemented") return "That is not available yet.";
    if (this.code === "internal") return "Something went wrong on our side.";
    return this.message;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      credentials: "include",
      headers: opts.body === undefined ? {} : { "content-type": "application/json" },
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError({ code: "network", status: 0, message: "Network request failed" });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const envelope = (parsed as { error?: { code?: string; message?: string; details?: Record<string, unknown>; requestId?: string } } | null)?.error;
    throw new ApiError({
      code: (envelope?.code as ApiErrorCode) ?? "internal",
      status: res.status,
      message: envelope?.message ?? `Request failed (${res.status})`,
      requestId: envelope?.requestId ?? res.headers.get("x-request-id"),
      ...(envelope?.details ? { details: envelope.details } : {}),
    });
  }
  return parsed as T;
}

export const api = {
  // --- auth --------------------------------------------------------------
  /** Sends a one-time sign-in link. The API answers 200 whether or not the address exists. */
  sendMagicLink: (email: string, callbackURL: string) =>
    request<{ status?: boolean }>("/api/auth/sign-in/magic-link", {
      method: "POST",
      body: { email, callbackURL },
    }),
  /**
   * Ask Better Auth where to send the browser for Google sign-in.
   *
   * This used to be an `<a href>` straight at the same path, on the reasoning that OAuth cannot
   * happen inside fetch. The redirect cannot, but this call can and must: `sign-in/social` is
   * POST-only, so the link produced a 404 and the button dead-ended. The endpoint answers
   * `{ url, redirect: true }` and the CALLER navigates, which is the shape Better Auth intends.
   */
  googleSignInUrl: async (callbackURL: string): Promise<string> => {
    const res = await request<{ url?: string; redirect?: boolean }>("/api/auth/sign-in/social", {
      method: "POST",
      body: { provider: "google", callbackURL },
    });
    if (!res.url) throw new Error("Google sign-in is not available on this deployment");
    return res.url;
  },
  signOut: () => request<unknown>("/api/auth/sign-out", { method: "POST", body: {} }),

  // --- account -----------------------------------------------------------
  me: (signal?: AbortSignal) => request<Me>("/api/me", signal ? { signal } : {}),
  deployment: () => request<{ deployment: DeploymentView | null }>("/api/deployment"),
  deploymentStatus: (signal?: AbortSignal) =>
    request<DeploymentStatusResponse>("/api/deployment/status", signal ? { signal } : {}),
  deploymentConnections: () => request<DeploymentConnectionsSummary>("/api/deployment/connections"),

  // --- billing -----------------------------------------------------------
  checkout: (input: { interval: "month" | "year"; promotionCode?: string; successUrl?: string; cancelUrl?: string }) =>
    request<{ sessionId: string; url: string }>("/api/billing/checkout", { method: "POST", body: input }),
  portal: (returnUrl?: string) =>
    request<{ url: string }>("/api/billing/portal", { method: "POST", body: returnUrl ? { returnUrl } : {} }),

  // --- connections -------------------------------------------------------
  connections: () => request<ConnectionsResponse>("/api/connections"),
  createConnection: (input: { displayName: string; clientType: string; capabilityProfile?: string }) =>
    request<IssuedConnection>("/api/connections", { method: "POST", body: input }),
  rotateConnection: (id: string) =>
    request<IssuedConnection>(`/api/connections/${encodeURIComponent(id)}/rotate`, { method: "POST", body: {} }),
  revokeConnection: (id: string) =>
    request<void>(`/api/connections/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // --- agent linking (OAuth consent, docs/PLAN-AGENT-LINKING.md §2.2) -----
  /**
   * The consent request an agent parked at `/oauth/authorize`. 404 when it is unknown, already
   * answered, or past its ten-minute window; the screen shows one message for all three.
   */
  consentRequest: (requestId: string) =>
    request<ConsentRequest>(`/api/oauth/consent/${encodeURIComponent(requestId)}`),
  /** Device leg: exchange the code the customer typed for the same consent request. */
  verifyDeviceCode: (userCode: string) =>
    request<ConsentRequest>("/api/oauth/device/verify", { method: "POST", body: { userCode } }),
  /**
   * Approving needs a session signed in within the last fifteen minutes; a stale one comes back as
   * 401 with `details.reason === "reauthentication_required"`, exactly like delete-confirm.
   */
  approveConsent: (requestId: string, capabilityProfile: string) =>
    request<ConsentDecision>(`/api/oauth/consent/${encodeURIComponent(requestId)}/approve`, {
      method: "POST",
      body: { capabilityProfile },
    }),
  denyConsent: (requestId: string) =>
    request<ConsentDecision>(`/api/oauth/consent/${encodeURIComponent(requestId)}/deny`, {
      method: "POST",
      body: {},
    }),

  // --- Hark --------------------------------------------------------------
  pairingStart: (deviceLabel?: string) =>
    request<PairingStart>("/api/pairing/hark/start", {
      method: "POST",
      body: deviceLabel ? { deviceLabel } : {},
    }),
  pairingComplete: (input: { pairingCode: string; secret: string; deviceLabel?: string }) =>
    request<PairingComplete>("/api/pairing/hark/complete", { method: "POST", body: input }),
  unpairHark: () => request<void>("/api/pairing/hark", { method: "DELETE" }),

  // --- export ------------------------------------------------------------
  exports: () => request<{ exports: ExportJob[] }>("/api/exports"),
  requestExport: () => request<{ export: ExportJob }>("/api/exports", { method: "POST", body: {} }),
  exportDownload: (id: string) =>
    request<{ url: string; expiresAt: string }>(`/api/exports/${encodeURIComponent(id)}/download-session`, {
      method: "POST",
      body: {},
    }),
  /** Cached server-side per deployment; `refresh` is for the "recheck" affordance only. */
  exportEstimate: (refresh = false) =>
    request<{ estimate: ExportEstimate }>(`/api/exports/estimate${refresh ? "?refresh=1" : ""}`),

  // --- transfer to the customer's own GitHub (plan §14.8) -----------------
  githubTransferState: () => request<GithubTransferState>("/api/exports/github-transfer"),
  /** Returns a GitHub URL for a full-page navigation; the App install cannot happen inside fetch. */
  githubConnect: () =>
    request<{ url: string }>("/api/exports/github-transfer/connect", { method: "POST", body: {} }),
  githubDisconnect: () => request<{ revoked: boolean }>("/api/exports/github-transfer", { method: "DELETE" }),
  startGithubTransfer: (input: { owner: string; repo: string; private?: boolean }) =>
    request<{ transfer: GithubTransfer }>("/api/exports/github-transfer/transfers", {
      method: "POST",
      body: input,
    }),
  githubTransfer: (id: string) =>
    request<{ transfer: GithubTransfer }>(`/api/exports/github-transfer/transfers/${encodeURIComponent(id)}`),

  // --- account lifecycle (API-CONTRACT §7; plan §15) ----------------------
  /**
   * Cancels BILLING. Nothing is deleted, and the deployment status in the response is the
   * CURRENT one — it moves to CANCELLATION_PENDING/RETAINED when Stripe reports period end,
   * not when this call returns.
   */
  cancelService: (reason?: string) =>
    request<CancelServiceResult>("/api/account/cancel-service", {
      method: "POST",
      body: reason ? { reason } : {},
    }),
  /**
   * Three outcomes (see `ReactivateResult.outcome`). Two of them hand back a URL the caller
   * must send the customer to; only `resubscribed` completes without a redirect.
   */
  reactivate: (returnUrl?: string) =>
    request<ReactivateResult>("/api/account/reactivate", {
      method: "POST",
      body: returnUrl ? { returnUrl } : {},
    }),
  /** Step 1 of 2. Records an intent; destroys nothing. */
  deleteRequest: (confirmEmail: string) =>
    request<DeleteRequestResult>("/api/account/delete-request", {
      method: "POST",
      body: { confirmEmail, acknowledge: true },
    }),
  /**
   * Step 2 of 2. Requires a session signed in within the last few minutes; a stale one comes
   * back as 401 with `details.reason === "reauthentication_required"`.
   */
  deleteConfirm: (confirmToken: string) =>
    request<{ deployment: { id: string; status: string }; message: string }>("/api/account/delete-confirm", {
      method: "POST",
      body: { confirmToken },
    }),

  // --- operator (plan §26) -----------------------------------------------
  // Routed through the API's own operator gate; a non-operator session gets 403.
  adminDeployments: (query: Record<string, string> = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request<{ deployments: unknown[] }>(`/api/admin/deployments${qs ? `?${qs}` : ""}`);
  },
  adminDeployment: (id: string) =>
    request<Record<string, unknown>>(`/api/admin/deployments/${encodeURIComponent(id)}`),
  adminRetry: (id: string) =>
    request<{ runId: string; attempt: number }>(`/api/admin/deployments/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      body: {},
    }),
  adminAudit: (query: Record<string, string> = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request<{ events: unknown[] }>(`/api/admin/audit${qs ? `?${qs}` : ""}`);
  },
  adminCreateCoupon: (input: {
    code: string;
    percentOff: number;
    durationMonths?: number;
    maxRedemptions?: number;
    trialDays?: number;
  }) => request<{ promotion: Record<string, unknown> }>("/api/admin/promotions", { method: "POST", body: input }),
  adminPromotions: () => request<{ promotions: unknown[] }>("/api/admin/promotions"),
};

export { request as rawRequest };
